import { useEffect, useState } from "react";
import { Copy, Trash2, UploadCloud } from "lucide-react";
import { mediaUrl } from "@/content/ContentContext";
import { apiDeleteMedia, apiListMedia, apiUploadMedia } from "@/admin/adminApi";

const formatSize = (bytes) => {
  if (bytes > 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const MediaTab = () => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const load = () => {
    apiListMedia()
      .then((res) => setItems(res.data))
      .catch(() => setMessage("Could not load media"));
  };

  useEffect(load, []);

  const handleUpload = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage("Image is larger than 5MB");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      await apiUploadMedia(file);
      load();
      setMessage("Uploaded");
    } catch (err) {
      setMessage("Upload failed");
    }
    setBusy(false);
  };

  const handleCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(mediaUrl(url));
      setMessage("URL copied");
    } catch (err) {
      setMessage(mediaUrl(url));
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiDeleteMedia(id);
      setItems((prev) => prev.filter((item) => item.id !== id));
      setMessage("Deleted");
    } catch (err) {
      setMessage("Delete failed");
    }
  };

  return (
    <div data-testid="admin-tab-content-media">
      <div className="af-media-toolbar">
        <label className="af-btn af-btn-primary">
          <UploadCloud size={14} />
          {busy ? "Uploading..." : "Upload image"}
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleUpload}
            data-testid="admin-media-upload-input"
          />
        </label>
        <span className="af-hint">PNG, JPG or WEBP, up to 5MB. Uploaded images can be used for the logo, hero and Why Choose Us backgrounds.</span>
        {message && <span className="af-media-message" data-testid="admin-media-message">{message}</span>}
      </div>
      {items.length === 0 ? (
        <p className="af-hint-block" data-testid="admin-media-empty">
          No uploads yet. Images you upload will appear here.
        </p>
      ) : (
        <div className="af-media-grid" data-testid="admin-media-grid">
          {items.map((item) => (
            <div className="af-media-card" key={item.id}>
              <div className="af-media-thumb">
                <img src={mediaUrl(`/api/media/${item.id}`)} alt={item.filename} loading="lazy" />
              </div>
              <div className="af-media-meta">
                <span className="af-media-name" title={item.filename}>{item.filename}</span>
                <span className="af-media-size">{formatSize(item.size)}</span>
              </div>
              <div className="af-media-actions">
                <button type="button" className="af-btn af-btn-ghost" onClick={() => handleCopy(`/api/media/${item.id}`)}>
                  <Copy size={12} />
                  Copy URL
                </button>
                <button type="button" className="af-btn af-btn-danger" onClick={() => handleDelete(item.id)}>
                  <Trash2 size={12} />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaTab;
